import React, { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { Col, Form, Select } from "antd";
import { useAllowedDealers } from "@/utils/entity";
import NotFoundContent from "@/components/NotFoundContent";

const { Option } = Select;

const DealerInput = ({
  formRef,
  itemName,
  disabled,
  hookDisabled,
  dealers: propDealers,
  rules,
  onChange,
  onDealersFetched,
  onHiddenChange,
  placeholder = "Select a dealer",
  searchText: propSearchText = "",
  dealerId: propDealerId,
  xs,
  sm,
  md,
  lg,
  xl,
  xxl,
}) => {
  const lastDealerIdPropRef = useRef(propDealerId);
  const lastSearchTextPropRef = useRef(propSearchText);
  const hasLoadedOnce = useRef(false);
  const searchTimeoutRef = useRef(null);
  const lastHiddenStatus = useRef(true);

  const [hidden, setHidden] = useState(lastHiddenStatus.current);
  const [searchText, setSearchText] = useState(lastSearchTextPropRef.current);
  const [dealerId, setDealerId] = useState(lastDealerIdPropRef.current);

  const {
    loading,
    dealers: localDealers,
    totalCount,
  } = useAllowedDealers({
    disabled: disabled || hookDisabled || Array.isArray(propDealers),
    searchText: searchText || dealerId,
  });

  const dealers = useMemo(() => (Array.isArray(propDealers) ? propDealers : localDealers), [propDealers, localDealers]);

  const handleSearch = useCallback(text => {
    if (searchTimeoutRef.current !== null) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchText(text);
      searchTimeoutRef.current = null;
    }, 500);
  }, []);

  const onSelect = useCallback(
    newDealerId => {
      if (typeof onChange !== "function") {
        return;
      }

      const dealer = dealers.filter(d => d.id === newDealerId)[0];

      if (dealer === undefined) {
        return;
      }

      setSearchText("");

      if (typeof onChange === "function") {
        onChange(dealer);
      }
    },
    [onChange, dealers],
  );

  const updateHidden = useCallback(
    newHidden => {
      setHidden(newHidden);

      if (lastHiddenStatus.current !== newHidden) {
        lastHiddenStatus.current = newHidden;

        if (typeof onHiddenChange === "function") {
          onHiddenChange(newHidden);
        }
      }
    },
    [onHiddenChange],
  );

  useEffect(() => {
    if (lastSearchTextPropRef.current !== propSearchText) {
      lastSearchTextPropRef.current = propSearchText;
      handleSearch(propSearchText);
    }
  }, [propSearchText, handleSearch]);

  useEffect(() => {
    if (lastDealerIdPropRef.current !== propDealerId) {
      lastDealerIdPropRef.current = propDealerId;
      setDealerId(propDealerId);
    }
  }, [propDealerId]);

  useEffect(() => {
    if (totalCount <= 1 && !lastSearchTextPropRef.current) {
      setTimeout(() => {
        // allow the browser to render the form to allow react to
        // create the form ref
        const dealer = dealers[0];
        if (!dealer) {
          return;
        }

        formRef?.current?.setFieldsValue({
          [itemName]: dealer.id,
        });
      });

      updateHidden(true);
    } else {
      updateHidden(false);
    }

    if (typeof onDealersFetched === "function") {
      onDealersFetched(dealers, searchText);
    }
  }, [dealers, itemName, totalCount, formRef, searchText, updateHidden, onDealersFetched]);

  return (
    <Col
      xs={hidden ? 0 : xs}
      sm={hidden ? 0 : sm}
      md={hidden ? 0 : md}
      lg={hidden ? 0 : lg}
      xl={hidden ? 0 : xl}
      xxl={hidden ? 0 : xxl}
    >
      <Form.Item name={itemName} label="Dealer" hidden={hidden} rules={rules}>
        <Select
          showSearch
          placeholder={placeholder}
          autoComplete="dontshow"
          disabled={disabled}
          onSearch={handleSearch}
          notFoundContent={NotFoundContent({ loading })}
          onBlur={() => {
            hasLoadedOnce.current = true;
          }}
          filterOption={(input, option) => option.key.toUpperCase().indexOf(input.toUpperCase()) !== -1}
          onSelect={onSelect}
          onFocus={() => {
            setDealerId(null);
          }}
          allowClear
        >
          {dealers.map(dealer => (
            <Option value={dealer.id} key={`${dealer.id || ""}${dealer.name || ""}`}>
              {dealer.name}
            </Option>
          ))}
        </Select>
      </Form.Item>
    </Col>
  );
};

DealerInput.defaultProps = {
  itemName: "dealerId",
  placeholder: "Select a dealer",
  searchText: "",
  dealerId: null,
  xs: 24,
  sm: 24,
  md: 24,
  lg: 12,
  xl: 12,
  xxl: 12,
  disabled: false,
  hookDisabled: false,
  dealers: undefined,
};

export default DealerInput;
